import i18n from 'i18next';
import type { TranslationType } from '@/types/api';
import { PROPERTY_APT_RESIDENT_en } from './overrides/PROPERTY_APT_RESIDENT.en';
import { PROPERTY_APT_RESIDENT_fi } from './overrides/PROPERTY_APT_RESIDENT.fi';
import { PROPERTY_APT_RESIDENT_sv } from './overrides/PROPERTY_APT_RESIDENT.sv';
import { PROPERTY_SPACE_PERSON_en } from './overrides/PROPERTY_SPACE_PERSON.en';
import { PROPERTY_SPACE_PERSON_fi } from './overrides/PROPERTY_SPACE_PERSON.fi';
import { PROPERTY_SPACE_PERSON_sv } from './overrides/PROPERTY_SPACE_PERSON.sv';

// Deep merge function to merge override translations with base translations
function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
        result[key] = deepMerge(targetValue || {}, sourceValue) as T[Extract<keyof T, string>];
      } else {
        result[key] = sourceValue as T[Extract<keyof T, string>];
      }
    }
  }

  return result;
}

/**
 * Applies translation overrides based on the company's translation type
 * @param translationType - The translation type from the company settings
 */
export function applyTranslationOverrides(translationType?: TranslationType | null) {
  // Store the current language
  const currentLang = i18n.language;

  if (!translationType || translationType === 'HOUSING_APT_RESIDENT') {
    // No overrides needed for the default translation type
    // Just ensure we're using the base translations (they're already loaded)
    return;
  }

  // Get the override translations based on a translation type
  const overrides = getTranslationOverrides(translationType);

  if (!overrides) {
    return;
  }

  // Apply overrides to each language
  Object.entries(overrides).forEach(([lang, translations]) => {
    // Get current translations
    const currentTranslations = i18n.getResourceBundle(lang, 'translation') || {};

    // Merge overrides with current translations
    const mergedTranslations = deepMerge(currentTranslations, translations);

    // Replace the resource bundle with merged translations
    i18n.addResourceBundle(lang, 'translation', mergedTranslations, true, true);
  });

  // Force re-render by changing language (if needed)
  if (i18n.language !== currentLang) {
    i18n.changeLanguage(currentLang);
  }
}

/**
 * Gets the translation overrides for a specific translation type
 * @param translationType - The translation type
 * @returns Object with translations for each language
 */
function getTranslationOverrides(translationType: TranslationType): Record<string, Record<string, unknown>> | null {
  switch (translationType) {
    case 'PROPERTY_APT_RESIDENT':
      return {
        en: PROPERTY_APT_RESIDENT_en.translation,
        fi: PROPERTY_APT_RESIDENT_fi.translation,
        sv: PROPERTY_APT_RESIDENT_sv.translation,
      };
    case 'PROPERTY_SPACE_PERSON':
      return {
        en: PROPERTY_SPACE_PERSON_en.translation,
        fi: PROPERTY_SPACE_PERSON_fi.translation,
        sv: PROPERTY_SPACE_PERSON_sv.translation,
      };
    case 'HOUSING_APT_RESIDENT':
    default:
      return null;
  }
}

/**
 * Sets the translation type in i18n options for tracking
 */
export function setTranslationType(translationType?: TranslationType | null) {
  (i18n.options as { translationType?: TranslationType | null }).translationType = translationType ?? null;
}
